import { MediaAssetType } from '@/types/media';
import { TemplateName } from '../studio-video-templates/types/template';
import { ZodError } from 'zod';
export interface BaseInputProps {
  title?: string;
  description?: string;
  isInvalid?: boolean;
  resetStore?: (skipFields?: string[]) => void;
}

export enum StepperInputName {
  ProductInput = 'ProductInput',
  ScriptInput = 'ScriptInput',
  TextInput = 'TextInput',
  AvatarInput = 'AvatarInput',
  MediaAssetsInput = 'MediaAssetsInput',
  SegmentedControlInput = 'SegmentedControlInput',
  TagSelectorInput = 'TagSelectorInput',
  MusicInput = 'MusicInput',
  CaptionsInput = 'CaptionsInput',
  TextOverlayInput = 'TextOverlayInput',
  BrandDescriptionInput = 'BrandDescriptionInput',
  PlatformInput = 'PlatformInput',
  AspectRatioInput = 'AspectRatioInput',
  TargetAudienceInput = 'TargetAudienceInput',
  PromoDetailsInput = 'PromoDetailsInput',
  Preview = 'Preview',
  ProductsImagesInput = 'ProductsImagesInput',
  AddModelInput = 'AddModelInput',
  RotationImagesInput = 'RotationImagesInput',
  MultiImageInput = 'MultiImageInput',
  ThemeSelectorInput = 'ThemeSelectorInput',
  ThemeApiRequest = 'ThemeApiRequest',
  ImageBackgroundsInput = 'ImageBackgroundsInput',
  ImageBackgroundsApiRequest = 'ImageBackgroundsApiRequest',
  VideoSettingsInput = 'VideoSettingsInput',
  MulticutShotsEditor = 'MulticutShotsEditor',
}

export interface MediaAsset {
  id: string;
  type: 'image' | 'video';
  thumbnailUrl: string;
}

export type StepperRequired = boolean | string | ((store: Record<string, any>) => boolean | string);

interface CompositionGuidance {
  defaultValues?: Record<string, any>;
  guidance?: string;
}

interface BaseInputConfig {
  id: string;
  name?: string;
  component: StepperInputName | string;
  title?: string;
  errorTitle?: string;
  description?: string;
  required?: StepperRequired;
  defaultValue?: any;
  uiProps?: Record<string, any>;
  compositionGuidance?: CompositionGuidance;
}

export type InputConfig = BaseInputConfig &
  (
    | {
        required: true | string | ((store: Record<string, any>) => boolean | string);
        errorMessage: string;
      }
    | {
        required?: false | undefined;
        errorMessage?: string;
      }
  );

export interface StepConfig {
  id: string;
  title: string;
  description?: string;
  inputs: InputConfig[];
  nextAction?: {
    text: string;
  };
}

export type CompositionAssetType = MediaAssetType | 'text' | 'slideshow' | 'avatar' | 'captions' | 'aspectRatio';

export interface CompositionLayout {
  width: number;
  height: number;
  captionsSettings?: {
    templateId: string;
  };
  tracks: Array<{
    comment?: string;
    includeCondition?: string;
    assets: Array<{
      id: string;
      type: string;
      duration: string | number;
      [key: string]: any;
    }>;
  }>;
}

export interface StepperConfig {
  id: TemplateName | string;
  llmInstructions?: string;
  compositionLayout?: CompositionLayout;
  generationType: StepperGenerationType | string;
  bulkCounterKey?: string;
  title: string;
  description?: string;
  steps: StepConfig[];
  variableMapper?: Record<string, string>;
  finalAction?: {
    text: string;
  };
}

export interface TemplateVariableDefinition {
  id: string;
  type: 'string' | 'number' | 'array' | 'object';
  required?: boolean;
  fields?: string[];
}

export interface TemplateConfig {
  title: string;
  description: string;
  variables: TemplateVariableDefinition[];
  input: {
    id: string;
    name: string;
    composition: any;
  };
}

/** The bridging transforms InputConfig => CompiledInput. with React components. */
export interface CompiledInput {
  id: string;
  name?: string;
  component: React.ComponentType<any>;
  required?: boolean;
  defaultValue?: any;
  title?: string;
  description?: string;
  uiProps: Record<string, any>;
  validate: (value: any, required?: boolean, errorMessage?: string) => boolean | ZodError;
  errorTitle?: string;
  errorMessage?: string;
}

export interface CompiledStep {
  id: string;
  title: string;
  description?: string;
  inputs: CompiledInput[];
  validate: (storeValues: Record<string, any>) => boolean;
}

export enum StepperGenerationType {
  studioVideo = 'studioVideo',
  bulkStudioVideo = 'bulkStudioVideo',
}

export interface StepperEntityData {
  id: string;
  createdAt: string;
  updatedAt: string;
  templateKey?: string;
  status?: string;
  stepperStore?: Record<string, any>;
  stepperConfig?: StepperConfig;
  name?: string;
}

export enum StepperMetaFields {
  generationType = 'generationType',
  entityId = 'entityId',
  templateKey = 'templateKey',
  status = 'status',
  entityName = 'entityName',
  bulkCounterKey = 'bulkCounterKey',
  createdAt = 'createdAt',
  updatedAt = 'updatedAt',
  shouldSyncGenerationsToTolstoy = 'shouldSyncGenerationsToTolstoy',
}

export enum SearchParamKeys {
  generationType = 'stepperGenerationType',
  template = 'template',
  referenceEntityId = 'id',
}

export enum StudioVideoVersion {
  v1 = '1.0.0',
  v2 = '2.0.0',
}
